import * as React from "react";
import { useParams } from "react-router-dom";
import {
    Badge,
    Banner,
    Button,
    Card,
    Checkbox,
    ConfirmModal,
    EmptyState,
    ErrorState,
    Icon,
    LoadingState,
    Modal,
    ModalBody,
    PageContainer,
    SearchInput,
    Select,
    Toolbar,
} from "../components";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { describeError, severityOf } from "../api/errors";
import * as Management from "../management/ManagementStore";
import { applyMarkWorkflow, MarkWorkflowAction } from "../activities/ActivitiesStore";
import { useStudentNames } from "../students/StudentDirectory";
import * as Store from "./MarkGridStore";
import { NewAssessmentModal } from "./Dialogs";
import { ExplanationPanel, TileBar, useClassFilter } from "./Shared";

/**
 * Every assessment in a division as one spreadsheet, with the division and module marks beside them - the legacy
 * SCADSYS mark sheet. Assessment cells are typed into; division, sub-division and module columns are the stored
 * results the calculation engine produced and are read-only, so a teacher sees the effect of a save without the
 * screen ever working a mark out itself.
 *
 * Large tertiary cohorts are the reason for the row windowing: past a few hundred rows only the visible slice is
 * rendered, and keyboard navigation scrolls the next row into view before focusing it.
 */

const ROW_HEIGHT = 41;
const WINDOW_THRESHOLD = 120;
const OVERSCAN = 12;

const cellKey = (columnKey: string, studentID: string) => `${columnKey}\u001f${studentID}`;

/** Teachers know finalizing as locking the marks; the API keeps its own names. */
const workflowLabels: Partial<Record<MarkWorkflowAction, string>> = { Finalize: "Lock", Unfinalize: "Unlock" };

const DivisionGrid: React.FC = () => {
    const { moduleID = "", groupID } = useParams();
    const [classID] = useClassFilter();
    const toast = useToast();
    const canEnter = usePermission(Permissions.MARKS_WRITE);
    const canFinalize = usePermission(Permissions.MARKS_FINALIZE);
    const canModerate = usePermission(Permissions.MARKS_MODERATE);
    const canManage = usePermission(Permissions.ACTIVITIES_WRITE);

    const [includeInactive, setIncludeInactive] = React.useState(false);
    const [entryMarkTypeID, setEntryMarkTypeID] = React.useState("");
    const [search, setSearch] = React.useState("");
    const [edits, setEdits] = React.useState<Record<string, string>>({});
    const [explaining, setExplaining] = React.useState<{ id: string; title: string } | null>(null);
    const [addingAssessment, setAddingAssessment] = React.useState(false);
    const [rejecting, setRejecting] = React.useState(false);

    const sheet = useAsync(
        () =>
            Store.getSheet({
                moduleID,
                groupID,
                teachingGroupID: classID || undefined,
                includeInactive,
                entryMarkTypeID: entryMarkTypeID || undefined,
            }),
        [moduleID, groupID, classID, includeInactive, entryMarkTypeID]
    );
    const teachingGroups = useAsync(() => Management.listTeachingGroups(), [], { initial: [] });
    const groups = React.useMemo(
        () => (teachingGroups.data ?? []).filter((g) => g.qualificationModuleID === moduleID).map((g) => ({ id: g.id, name: g.name })),
        [teachingGroups.data, moduleID]
    );

    // A typed mark belongs to one sheet. Carrying it across to another division, class or mark type would write it
    // to the wrong place, so pending edits are dropped whenever the sheet's identity changes.
    React.useEffect(() => setEdits({}), [moduleID, groupID, classID, entryMarkTypeID]);

    const data = sheet.data;
    const columns = data?.columns ?? [];
    const rawRows = data?.rows ?? [];
    const nameOf = useStudentNames(rawRows.map((r) => r.studentID));

    // Names arrive from the identity server after the sheet does; keying on them re-sorts once they land.
    const names = rawRows.map((r) => nameOf(r.studentID));
    const namesKey = names.join("\u001f");
    const rows = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        return rawRows
            .map((r, i) => ({ row: r, name: names[i] }))
            .filter((r) => !term || r.name.toLowerCase().includes(term) || (r.row.studentID ?? "").toLowerCase().includes(term))
            .sort((a, b) => a.name.localeCompare(b.name));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawRows, search, namesKey]);

    // ------------------------------------------------------------- edits

    const columnByKey = React.useMemo(() => new Map(columns.map((c) => [c.key ?? "", c])), [columns]);

    const pending = React.useMemo(() => {
        const valid: Array<{ column: Store.MarkGridColumnModel; studentID: string; value: number }> = [];
        const invalid = new Set<string>();
        Object.entries(edits).forEach(([key, text]) => {
            const [columnKey, studentID] = key.split("\u001f");
            const column = columnByKey.get(columnKey);
            if (!column || text.trim() === "") return;
            const value = Number(text);
            const existing = rawRows.find((r) => r.studentID === studentID)?.cells?.[columnKey]?.result;
            if (!Number.isFinite(value) || value < 0 || (column.total !== undefined && value > column.total)) {
                invalid.add(key);
                return;
            }
            if (existing !== value) valid.push({ column, studentID, value });
        });
        return { valid, invalid };
    }, [edits, columnByKey, rawRows]);

    const dirty = pending.valid.length > 0;

    React.useEffect(() => {
        if (!dirty) return;
        const warn = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);

    const save = useAction(async () => {
        const byColumn = new Map<string, Store.ColumnEntries>();
        pending.valid.forEach(({ column, studentID, value }) => {
            const entry = byColumn.get(column.key ?? "") ?? {
                entityType: column.entityType ?? "Assessment",
                entityID: column.entityID ?? "",
                markTypeID: column.markTypeID ?? "",
                entries: [],
            };
            entry.entries.push({ studentID, result: value });
            byColumn.set(column.key ?? "", entry);
        });

        const result = await Store.saveMarks(Array.from(byColumn.values()), classID || undefined);
        const errors = (result.recalculationFindings ?? []).filter((f) => severityOf(f as any) === "Error");
        if (errors.length) toast.warning(`Saved ${result.savedMarks} mark(s), but recalculation reported ${errors.length} error(s). Check Findings.`);
        else toast.success(`Saved ${result.savedMarks} mark(s).`);
        setEdits({});
        sheet.refresh();
    });

    // ---------------------------------------------------------- workflow

    const runWorkflow = (action: MarkWorkflowAction, comment?: string) =>
        applyMarkWorkflow(action, [{ kind: "QualificationModule", ids: [moduleID] }], comment).then((result) => {
            toast.success(`${workflowLabels[action] ?? action} applied to ${result.affectedResults ?? 0} result(s).`);
            sheet.refresh();
        });

    const submit = useAction(() => runWorkflow("Submit"));
    const approve = useAction(() => runWorkflow("Approve"));
    const finalize = useAction(() => runWorkflow("Finalize"));
    const unfinalize = useAction(() => runWorkflow("Unfinalize"));
    const [rejectBusy, setRejectBusy] = React.useState(false);
    const [rejectError, setRejectError] = React.useState("");
    const confirmReject = async (comment: string) => {
        setRejectBusy(true);
        setRejectError("");
        try {
            await runWorkflow("Reject", comment);
            setRejecting(false);
        } catch (e) {
            setRejectError(describeError(e));
        } finally {
            setRejectBusy(false);
        }
    };

    // ------------------------------------------------------ deletion

    // An assessment created by mistake. The counts come from the sheet on screen, which is filtered by class, so the
    // server re-checks everything: it refuses finalized marks, marks without the delete-marks permission, and an
    // assessment a procedure names directly.
    const canDeleteMarks = usePermission(Permissions.MARKS_DELETE);
    const [deleting, setDeleting] = React.useState<Store.MarkGridColumnModel | null>(null);
    const [deleteBusy, setDeleteBusy] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");
    const recordedOf = (column: Store.MarkGridColumnModel) => {
        const cells = rawRows.map((r) => r.cells?.[column.key ?? ""]).filter((c) => c?.result !== undefined && c?.result !== null);
        return { marks: cells.length, locked: cells.filter((c) => c?.finalizationStatus === "Finalized").length };
    };
    /** Why the list's Delete is disabled, said on the row itself rather than after the click. */
    const deleteBlockedReason = (column: Store.MarkGridColumnModel) => {
        const { marks, locked } = recordedOf(column);
        if (locked > 0) return `${locked} locked mark${locked === 1 ? "" : "s"} - unlock before deleting.`;
        if (marks > 0 && !canDeleteMarks) return "Has marks - deleting needs the delete-marks permission.";
        return undefined;
    };
    const { marks: deletingMarks, locked: deletingFinalized } = deleting ? recordedOf(deleting) : { marks: 0, locked: 0 };
    const openDelete = (column: Store.MarkGridColumnModel) => {
        setDeleteError("");
        setDeleting(column);
    };
    const confirmDelete = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        setDeleteError("");
        try {
            // Confirming here is consent to remove whatever marks and comments it has, including ones outside the
            // class filter - the body says so.
            const result = await Store.deleteAssessment(deleting.entityID ?? "", true);
            const removed = (result.removedMarks ?? 0) + (result.removedComments ?? 0);
            toast.success(removed ? `Deleted ${deleting.name} and ${removed} recorded result(s).` : `Deleted ${deleting.name}.`);
            setDeleting(null);
            setEdits((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith(`${deleting.key}\u001f`))));
            sheet.refresh();
        } catch (e) {
            setDeleteError(describeError(e));
        } finally {
            setDeleteBusy(false);
        }
    };

    // ------------------------------------------------------ windowing

    const scroller = React.useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = React.useState(0);
    const [viewport, setViewport] = React.useState(600);
    React.useEffect(() => {
        const el = scroller.current;
        if (!el) return;
        const measure = () => setViewport(el.clientHeight || 600);
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [data]);

    const windowed = rows.length > WINDOW_THRESHOLD;
    const first = windowed ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN) : 0;
    const last = windowed ? Math.min(rows.length, Math.ceil((scrollTop + viewport) / ROW_HEIGHT) + OVERSCAN) : rows.length;

    const editableColumns = columns.map((c, i) => (c.isEditable ? i : -1)).filter((i) => i >= 0);

    const focusCell = (rowIndex: number, columnIndex: number) => {
        if (rowIndex < 0 || rowIndex >= rows.length) return;
        const find = () => scroller.current?.querySelector<HTMLInputElement>(`[data-cell="${rowIndex}:${columnIndex}"]`);
        const target = find();
        if (target) {
            target.focus();
            target.select();
            return;
        }
        // The row is outside the rendered window: scroll it in, then focus once it has rendered.
        if (scroller.current) scroller.current.scrollTop = Math.max(0, rowIndex * ROW_HEIGHT - viewport / 2);
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const el = find();
            el?.focus();
            el?.select();
        }));
    };

    const onCellKey = (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, columnIndex: number) => {
        if (e.key === "Enter" || e.key === "ArrowDown") {
            e.preventDefault();
            focusCell(rowIndex + (e.shiftKey && e.key === "Enter" ? -1 : 1), columnIndex);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            focusCell(rowIndex - 1, columnIndex);
        } else if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && e.currentTarget.selectionStart === e.currentTarget.selectionEnd) {
            const atEnd = e.currentTarget.selectionStart === e.currentTarget.value.length;
            const atStart = e.currentTarget.selectionStart === 0;
            const position = editableColumns.indexOf(columnIndex);
            if (e.key === "ArrowRight" && atEnd && position < editableColumns.length - 1) {
                e.preventDefault();
                focusCell(rowIndex, editableColumns[position + 1]);
            } else if (e.key === "ArrowLeft" && atStart && position > 0) {
                e.preventDefault();
                focusCell(rowIndex, editableColumns[position - 1]);
            }
        }
    };

    // ----------------------------------------------------------- render

    if (sheet.isLoading && !data) return <PageContainer><LoadingState label="Loading mark sheet..." /></PageContainer>;
    if (sheet.error && !data) return <PageContainer><ErrorState message={sheet.error} onRetry={sheet.refresh} /></PageContainer>;
    if (!data) return null;

    const title = data.groupName ?? data.moduleName ?? "";
    const groupTotal = columns.find((c) => c.kind === Store.ColumnKinds.GroupTotal);

    // Header row one: consecutive assessment columns of the same division share a spanning heading; result columns
    // span both name rows so the sheet reads like the legacy one.
    const headerGroups: Array<{ label: string; span: number; column?: Store.MarkGridColumnModel }> = [];
    columns.forEach((c) => {
        if (c.kind !== Store.ColumnKinds.Assessment) {
            headerGroups.push({ label: c.name ?? "", span: 1, column: c });
            return;
        }
        const previous = headerGroups[headerGroups.length - 1];
        if (previous && !previous.column && previous.label === (c.groupName ?? "")) previous.span++;
        else headerGroups.push({ label: c.groupName ?? "", span: 1 });
    });

    const resultLabel = (c: Store.MarkGridColumnModel) =>
        c.kind === Store.ColumnKinds.ModuleTotal ? c.markTypeName ?? "Total" : c.kind === Store.ColumnKinds.GroupTotal ? `${c.markTypeName ?? "Total"}` : "Subtotal";

    const columnAverage = (c: Store.MarkGridColumnModel) => {
        const values = rows
            .map((r) => r.row.cells?.[c.key ?? ""])
            .map((cell) => (c.kind === Store.ColumnKinds.Assessment ? cell?.result : Store.percentOf(cell)))
            .filter((v): v is number => v !== undefined && v !== null);
        return values.length ? values.reduce((a, b) => a + b, 0) / values.length : undefined;
    };

    const moduleTotal = columns.find((c) => c.kind === Store.ColumnKinds.ModuleTotal);
    const editCount = Object.keys(edits).length;
    const deletable = columns.filter((c) => c.kind === Store.ColumnKinds.Assessment && !!c.entityID);
    const showWorkflow = canEnter || canModerate || canFinalize;
    const workflowErrors = [submit, approve, finalize, unfinalize].map((a) => a.error).filter(Boolean);

    return (
        <PageContainer>
            <TileBar
                moduleID={moduleID}
                moduleName={data.moduleName ?? ""}
                path={data.path ?? []}
                teachingGroups={groups}
                terms={data.terms ?? []}
                current="sheet"
            />

            <div className="mb-4 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                <div className="min-w-0">
                    <h1 className="text-xl font-semibold text-text-strong">{title}</h1>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {data.cycleName && <Badge tone="neutral">{data.cycleName}</Badge>}
                        {groupTotal?.weightPercent !== undefined && groupTotal?.weightPercent !== null && (
                            <Badge tone="primary">
                                {Store.formatMark(groupTotal.weightPercent)}% of {moduleTotal?.markTypeName ?? "the module mark"}
                            </Badge>
                        )}
                        {data.entryMarkTypeName && <Badge tone="info">Capturing {data.entryMarkTypeName}</Badge>}
                        {classID && <Badge tone="neutral">One class</Badge>}
                    </div>
                </div>

                {showWorkflow && (
                    <div className="flex flex-col items-start gap-1 sm:items-end">
                        <span className="text-sm text-text-weak" title="These actions apply to every mark in this subject, not just this division.">
                            Subject workflow · every division
                        </span>
                        <div role="group" aria-label="Subject workflow" className="flex flex-wrap gap-2 sm:justify-end">
                            {canEnter && (
                                <Button variant="outline-secondary" icon={["fad", "paper-plane"]} onClick={submit.run} loading={submit.busy}>
                                    Submit
                                </Button>
                            )}
                            {canModerate && (
                                <>
                                    <Button variant="outline-success" icon={["fad", "circle-check"]} onClick={approve.run} loading={approve.busy}>
                                        Approve
                                    </Button>
                                    <Button variant="outline-danger" icon={["fad", "circle-xmark"]} onClick={() => setRejecting(true)}>
                                        Reject
                                    </Button>
                                </>
                            )}
                            {canFinalize && (
                                <>
                                    <Button variant="outline-primary" icon={["fad", "lock"]} onClick={finalize.run} loading={finalize.busy} title="Lock every mark in this subject">
                                        Lock
                                    </Button>
                                    <Button variant="outline-secondary" icon={["fad", "lock-open"]} onClick={unfinalize.run} loading={unfinalize.busy} title="Unlock every mark in this subject">
                                        Unlock
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {!data.entryMarkTypeID && (
                <Banner tone="warning" className="mb-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <span>No calculation procedure on this subject says which mark type assessments are captured under. Pick one to enter marks:</span>
                        <Select value={entryMarkTypeID} placeholder="Mark type" onChange={(e) => setEntryMarkTypeID(e.target.value)} className="w-56">
                            {(data.markTypes ?? []).map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                </Banner>
            )}
            {save.error && <Banner tone="danger" className="mb-3">{save.error}</Banner>}
            {workflowErrors.map((error, i) => (
                <Banner key={i} tone="danger" className="mb-3">
                    {error}
                </Banner>
            ))}

            <Card>
                <Toolbar className="!mb-0 border-b border-border px-3 py-2">
                    <SearchInput value={search} onChange={setSearch} placeholder="Find a student..." className="max-w-xs" />
                    <Checkbox label="Include inactive" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
                    <div className="ml-auto flex flex-wrap items-center gap-2">
                        <span className="text-sm text-text-weak">
                            {rows.length} student{rows.length === 1 ? "" : "s"}
                        </span>
                        {pending.valid.length > 0 && <Badge tone="warning">{pending.valid.length} unsaved</Badge>}
                        {pending.invalid.size > 0 && (
                            <Badge tone="danger" title="A mark must be a number between 0 and the assessment's total.">
                                {pending.invalid.size} invalid
                            </Badge>
                        )}
                        {canEnter && editCount > 0 && (
                            <Button variant="outline-secondary" icon={["fad", "xmark"]} onClick={() => setEdits({})} disabled={save.busy}>
                                Discard
                            </Button>
                        )}
                        {canEnter && (
                            <Button
                                icon={["fad", "floppy-disk"]}
                                onClick={save.run}
                                loading={save.busy}
                                disabled={!dirty || pending.invalid.size > 0}
                                title={pending.invalid.size > 0 ? "Fix the highlighted marks first" : undefined}
                            >
                                Save {pending.valid.length || ""} mark{pending.valid.length === 1 ? "" : "s"}
                            </Button>
                        )}
                    </div>
                </Toolbar>

                {columns.length === 0 ? (
                    <EmptyState
                        icon={["fad", "table-list"]}
                        title="No assessments in this division"
                        hint={canManage ? "Add one with New assessment." : "Ask an administrator to add assessments to this division."}
                    />
                ) : rawRows.length === 0 ? (
                    <EmptyState
                        icon={["fad", "users-slash"]}
                        title="No students on this sheet"
                        hint={classID ? "That class has no enrolled members in this subject." : "Nobody is enrolled in this subject yet."}
                    />
                ) : (
                    <div
                        ref={scroller}
                        className="max-h-[calc(100dvh-20rem)] min-h-[20rem] overflow-auto"
                        onScroll={(e) => windowed && setScrollTop(e.currentTarget.scrollTop)}
                    >
                        <table className="w-full border-collapse text-sm">
                            <thead className="sticky top-0 z-10 bg-surface-muted text-left text-text">
                                <tr>
                                    <th rowSpan={3} className="sticky left-0 z-20 w-10 border-b border-r border-border bg-surface-muted px-2 py-2 align-bottom text-xs font-medium text-text-weak">
                                        #
                                    </th>
                                    <th rowSpan={3} className="sticky left-10 z-20 min-w-[12rem] border-b border-r border-border bg-surface-muted px-3 py-2 align-bottom">
                                        Student
                                    </th>
                                    {headerGroups.map((h, i) =>
                                        h.column ? (
                                            <th
                                                key={i}
                                                rowSpan={2}
                                                className="min-w-[5.5rem] border-b border-l border-border bg-primary-soft px-2 py-2 text-right align-bottom font-semibold text-primary-text"
                                            >
                                                <div className="truncate" title={h.label}>
                                                    {h.label}
                                                </div>
                                                <div className="text-xs font-normal opacity-80">{resultLabel(h.column)}</div>
                                            </th>
                                        ) : (
                                            <th key={i} colSpan={h.span} className="border-b border-l border-border px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-text-weak">
                                                {h.label}
                                            </th>
                                        )
                                    )}
                                </tr>
                                <tr>
                                    {columns
                                        .filter((c) => c.kind === Store.ColumnKinds.Assessment)
                                        .map((c) => (
                                            <th key={c.key} className="min-w-[5.5rem] border-b border-l border-border px-2 py-1.5 align-bottom font-medium text-text-strong">
                                                <div className="max-w-[9rem] truncate" title={c.name}>
                                                    {c.name}
                                                </div>
                                            </th>
                                        ))}
                                </tr>
                                <tr>
                                    {columns.map((c) => (
                                        <th key={c.key} className="border-b border-l border-border px-2 py-1 text-right text-xs font-normal text-text-weak">
                                            {c.kind === Store.ColumnKinds.Assessment ? (
                                                `/ ${Store.formatMark(c.total)}`
                                            ) : (
                                                <>
                                                    %
                                                    {c.weightPercent !== undefined && c.weightPercent !== null && (
                                                        <span className="ml-1 opacity-80" title="Weight in the mark above it">
                                                            · w {Store.formatMark(c.weightPercent)}%
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {first > 0 && <tr style={{ height: first * ROW_HEIGHT }} aria-hidden />}
                                {rows.slice(first, last).map(({ row, name }, offset) => {
                                    const rowIndex = first + offset;
                                    return (
                                        <tr
                                            key={row.studentID}
                                            style={{ height: ROW_HEIGHT }}
                                            className="group border-b border-border hover:bg-surface-hover/50 focus-within:bg-primary-soft/60"
                                        >
                                            <td className="sticky left-0 border-r border-border bg-surface px-2 text-xs tabular-nums text-text-weak group-focus-within:bg-primary-soft group-focus-within:font-semibold group-focus-within:text-primary-text">
                                                {rowIndex + 1}
                                            </td>
                                            <td className="sticky left-10 border-r border-border bg-surface px-3 group-focus-within:bg-primary-soft" title={row.studentID}>
                                                <span className="font-medium text-text-strong">{name}</span>
                                                {row.enrollmentStatus && row.enrollmentStatus !== "Enrolled" && (
                                                    <Badge tone="neutral" className="ml-2">
                                                        {row.enrollmentStatus}
                                                    </Badge>
                                                )}
                                            </td>
                                            {columns.map((c, columnIndex) => {
                                                const cell = row.cells?.[c.key ?? ""];
                                                const key = cellKey(c.key ?? "", row.studentID ?? "");
                                                if (c.kind === Store.ColumnKinds.Assessment) {
                                                    const editable = canEnter && !!c.isEditable && cell?.isEditable !== false;
                                                    if (!editable) {
                                                        return (
                                                            <td key={c.key} className="border-l border-border px-2 text-right tabular-nums text-text" title={cell?.notEditableReason}>
                                                                {cell?.notEditableReason && <Icon icon={["fad", "lock"]} className="mr-1 text-xs text-text-weak" />}
                                                                {Store.formatMark(cell?.result)}
                                                            </td>
                                                        );
                                                    }
                                                    const value = edits[key] ?? (cell?.result === undefined || cell?.result === null ? "" : String(cell.result));
                                                    const isInvalid = pending.invalid.has(key);
                                                    const isDirty = edits[key] !== undefined && edits[key].trim() !== "" && Number(edits[key]) !== cell?.result;
                                                    return (
                                                        <td key={c.key} className="border-l border-border px-1">
                                                            <input
                                                                data-cell={`${rowIndex}:${columnIndex}`}
                                                                inputMode="decimal"
                                                                value={value}
                                                                aria-label={`${c.name} for ${name}`}
                                                                aria-invalid={isInvalid || undefined}
                                                                title={isInvalid ? `Enter a number from 0 to ${Store.formatMark(c.total)}` : undefined}
                                                                onChange={(e) => setEdits((current) => ({ ...current, [key]: e.target.value }))}
                                                                onKeyDown={(e) => onCellKey(e, rowIndex, columnIndex)}
                                                                onFocus={(e) => e.currentTarget.select()}
                                                                className={`h-8 w-full min-w-[4rem] rounded border px-2 text-right tabular-nums text-text-strong outline-none focus:border-primary focus:bg-surface focus:ring-2 focus:ring-ring ${
                                                                    isInvalid
                                                                        ? "border-danger bg-danger-soft"
                                                                        : isDirty
                                                                          ? "border-warning-border bg-warning-soft"
                                                                          : "border-transparent bg-transparent hover:border-border"
                                                                }`}
                                                            />
                                                        </td>
                                                    );
                                                }
                                                const percent = Store.percentOf(cell);
                                                return (
                                                    <td key={c.key} className="border-l border-border bg-primary-soft/40 px-2 text-right font-medium tabular-nums text-text-strong">
                                                        {cell?.resultOrigin === "Calculated" && cell.markResultID ? (
                                                            <button
                                                                type="button"
                                                                className="underline decoration-dotted underline-offset-2 hover:text-primary-text"
                                                                title="How was this calculated?"
                                                                onClick={() => setExplaining({ id: cell.markResultID!, title: `${c.name} - ${name}` })}
                                                            >
                                                                {Store.formatMark(percent)}
                                                            </button>
                                                        ) : (
                                                            <span title={cell?.resultOrigin}>{Store.formatMark(percent)}</span>
                                                        )}
                                                        {cell?.gradeCode && <span className="ml-1 text-xs font-normal text-text-weak">{cell.gradeCode}</span>}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                {last < rows.length && <tr style={{ height: (rows.length - last) * ROW_HEIGHT }} aria-hidden />}
                            </tbody>
                            <tfoot className="sticky bottom-0 bg-surface-muted text-text">
                                <tr>
                                    <td className="sticky left-0 border-t border-border bg-surface-muted" />
                                    <td className="sticky left-10 border-r border-t border-border bg-surface-muted px-3 py-2 text-xs font-semibold uppercase tracking-wide text-text-weak">
                                        Average
                                    </td>
                                    {columns.map((c) => (
                                        <td key={c.key} className="border-l border-t border-border px-2 py-2 text-right text-xs font-semibold tabular-nums">
                                            {Store.formatMark(columnAverage(c))}
                                        </td>
                                    ))}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </Card>

            {columns.length > 0 && rawRows.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-weak">
                    <Swatch className="border-warning-border bg-warning-soft">Unsaved</Swatch>
                    <Swatch className="border-danger bg-danger-soft">Invalid</Swatch>
                    <Swatch className="border-primary-tint bg-primary-soft">Calculated - click for the working</Swatch>
                    <span className="inline-flex items-center gap-1.5">
                        <Icon icon={["fad", "lock"]} /> Locked - hover for the reason
                    </span>
                    <span className="ml-auto hidden sm:inline">Enter / ↑ ↓ move between students · ← → between assessments</span>
                </div>
            )}

            {canManage && (groupID || deletable.length > 0) && (
                <Card className="mt-6">
                    <div className="flex flex-wrap items-center gap-4 border-b border-border px-5 py-4">
                        <div className="mr-auto">
                            <h3 className="text-lg font-semibold text-text-strong">Assessments</h3>
                            <p className="text-base text-text-weak">
                                Deleting an assessment removes its marks and recalculates the division and subject marks.
                                {classID ? " Mark counts here are for the selected class only." : ""}
                            </p>
                        </div>
                        {groupID && (
                            <Button size="lg" variant="outline-primary" icon={["fad", "file-circle-plus"]} onClick={() => setAddingAssessment(true)}>
                                New assessment
                            </Button>
                        )}
                    </div>
                    {deletable.length === 0 ? (
                        <p className="px-5 py-4 text-base text-text-weak">No assessments in this division yet.</p>
                    ) : (
                        <ul className="divide-y divide-border">
                            {deletable.map((c) => {
                                const { marks, locked } = recordedOf(c);
                                const blocked = deleteBlockedReason(c);
                                return (
                                    <li key={c.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
                                        <div className="min-w-[12rem] flex-1">
                                            <div className="truncate text-base font-medium text-text-strong" title={c.name}>
                                                {c.name}
                                            </div>
                                            <div className="text-sm text-text-weak">
                                                {c.groupName && c.groupName !== title ? `${c.groupName} · ` : ""}Out of {Store.formatMark(c.total)}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge tone={marks ? "neutral" : "success"}>
                                                {marks ? `${marks} mark${marks === 1 ? "" : "s"} recorded` : "No marks yet"}
                                            </Badge>
                                            {locked > 0 && <Badge tone="warning">{locked} locked</Badge>}
                                        </div>
                                        {blocked && <span className="text-sm text-text-weak">{blocked}</span>}
                                        <Button
                                            size="lg"
                                            variant="outline-danger"
                                            icon={["fad", "trash"]}
                                            disabled={!!blocked}
                                            title={blocked}
                                            aria-label={`Delete assessment ${c.name}`}
                                            onClick={() => openDelete(c)}
                                        >
                                            Delete
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Card>
            )}

            <Modal isOpen={!!explaining} onClose={() => setExplaining(null)} title={explaining?.title} icon={["fad", "calculator"]} size="xl">
                <ModalBody>{explaining && <ExplanationPanel markResultID={explaining.id} />}</ModalBody>
            </Modal>

            {groupID && (
                <NewAssessmentModal
                    isOpen={addingAssessment}
                    onClose={() => setAddingAssessment(false)}
                    groupID={groupID}
                    groupName={title}
                    onCreated={sheet.refresh}
                />
            )}

            <ConfirmModal
                isOpen={rejecting}
                onClose={() => setRejecting(false)}
                onConfirm={confirmReject}
                title="Reject marks"
                icon={["fad", "circle-xmark"]}
                confirmLabel="Reject"
                busy={rejectBusy}
                error={rejectError}
                requireComment
                commentLabel="Reason"
                commentPlaceholder="Why are these marks being sent back?"
                body="Rejecting sends every mark in this subject back. The server refuses a blank reason, so one is required here."
            />

            <ConfirmModal
                isOpen={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={confirmDelete}
                title={`Delete ${deleting?.name ?? "assessment"}`}
                icon={["fad", "trash"]}
                confirmLabel={deletingMarks ? `Delete with ${deletingMarks} mark(s)` : "Delete"}
                busy={deleteBusy}
                confirmDisabled={deletingFinalized > 0 || (deletingMarks > 0 && !canDeleteMarks)}
                error={deleteError}
                body={
                    deletingFinalized > 0 ? (
                        <>
                            {deletingFinalized} mark(s) on this assessment are locked. Unlock them before deleting it.
                        </>
                    ) : deletingMarks > 0 && !canDeleteMarks ? (
                        <>
                            This assessment already has {deletingMarks} mark(s). Deleting it would remove them, which needs the delete-marks
                            permission - ask someone who has it.
                        </>
                    ) : (
                        <>
                            <p>
                                The assessment and its place in the division's calculation are removed, and the division and subject marks are
                                recalculated. This cannot be undone.
                            </p>
                            {deletingMarks > 0 && (
                                <p className="mt-2 font-medium text-danger-text">
                                    {deletingMarks} recorded mark(s) shown here - and any for students outside the current class filter, plus any
                                    comments on it - will be deleted with it.
                                </p>
                            )}
                        </>
                    )
                }
            />
        </PageContainer>
    );
};

/** A legend entry: a small sample of how a cell in that state is drawn. */
const Swatch: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
    <span className="inline-flex items-center gap-1.5">
        <span aria-hidden className={`inline-block h-3 w-3 rounded-sm border ${className}`} />
        {children}
    </span>
);

export default DivisionGrid;
